import {
    Component,
    computed,
    inject,
    input,
    output,
    signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { Checkbox } from 'primeng/checkbox';
import { Dialog } from 'primeng/dialog';
import { SelectButton } from 'primeng/selectbutton';
import { Message } from 'primeng/message';
import { ProgressSpinner } from 'primeng/progressspinner';
import { Textarea } from 'primeng/textarea';
import { Tooltip } from 'primeng/tooltip';
import {
    AiAssistApiError,
    AiAssistService,
    AI_ASSIST_ICON,
    BaseArtCandidate,
    BaseArtResult,
    VENDOR_FLUX,
    VENDOR_RECRAFT,
} from '@/app/services/ai-assist.service';
import { BrandKitImage } from '@/app/services/brand-kit.service';

/** What the operator picks: the brand mark + its downscaled favicon (A2). */
export interface LogoPick {
    logoUrl: string;
    faviconUrl: string | null;
}

type Phase = 'form' | 'loading' | 'results';

/**
 * "Generate with AI" dialog for the realm brand icon (`brand_icon`). Everything
 * the model needs — text-free reference images, template id, style version, and
 * the style descriptor — comes from the assigned Site Template's brand-kit; the
 * references are shown read-only (no manual entry). Fires the async
 * `base/generate` job, polls it, and shows the candidate marks. Each candidate
 * carries a downscaled favicon (A2), so picking one yields both `LogoIconUrl`
 * and `FaviconUrl` via `(applied)`.
 */
@Component({
    selector: 'app-ai-logo-generate',
    standalone: true,
    imports: [
        FormsModule,
        Button,
        Checkbox,
        Dialog,
        SelectButton,
        Message,
        ProgressSpinner,
        Textarea,
        Tooltip,
    ],
    templateUrl: './ai-logo-generate.html',
})
export class AiLogoGenerate {
    private readonly aiAssist = inject(AiAssistService);

    readonly realmId = input.required<string>();
    /** Shown in the dialog header for context. */
    readonly brandName = input<string>('');
    /** Website-template StyleDescriptor (resolved from the brand-kit, or manual override). */
    readonly style = input<Record<string, unknown> | null>(null);

    /** Brand-kit assets — references (read-only images) + the template id/version
     *  the brand_icon is filed under. All come from the assigned Site Template. */
    readonly references = input<readonly BrandKitImage[]>([]);
    readonly templateId = input<string>('');
    readonly version = input<string>('');

    /** Disable the trigger (service down / missing template elements) + the why. */
    readonly disabled = input<boolean>(false);
    readonly disabledReason = input<string | null>(null);

    readonly applied = output<LogoPick>();

    protected readonly aiIcon = AI_ASSIST_ICON;

    protected readonly visible = signal(false);
    protected readonly phase = signal<Phase>('form');
    protected readonly error = signal<string | null>(null);

    protected readonly samples = signal(4);
    protected readonly sampleOptions = [
        { label: '1', value: 1 },
        { label: '2', value: 2 },
        { label: '3', value: 3 },
        { label: '4', value: 4 },
    ];
    protected readonly useManualPrompt = signal(false);
    protected readonly prompt = signal('');

    protected readonly candidates = signal<BaseArtCandidate[]>([]);

    private abort: AbortController | null = null;

    /**
     * Non-null when a vendor required by brand_icon (FLUX + Recraft) is out of
     * credits — a UX pre-check mirroring the service's pre-flight gate. Blocks
     * the Generate button so the operator sees why before hitting a 400.
     */
    protected readonly creditBlock = computed<string | null>(() => {
        const blocked = this.aiAssist
            .blockedVendors()
            .filter((v) => v === VENDOR_FLUX || v === VENDOR_RECRAFT);
        if (blocked.length === 0) return null;
        const names = blocked
            .map((v) => (v === VENDOR_FLUX ? 'FLUX' : 'Recraft'))
            .join(' and ');
        const verb = blocked.length > 1 ? 'are' : 'is';
        return `Generation paused — ${names} ${verb} out of credits. Top up the vendor account to continue.`;
    });

    protected readonly canGenerate = computed<boolean>(() => {
        const refs = this.references().length;
        if (refs < 1 || refs > 8) return false;
        if (!this.templateId().trim() || !this.version().trim()) return false;
        if (this.useManualPrompt() && !this.prompt().trim()) return false;
        if (this.creditBlock()) return false;
        return true;
    });

    protected open(): void {
        if (this.disabled()) return;
        this.reset();
        void this.aiAssist.refreshVendorBalances();
        this.visible.set(true);
    }

    protected onVisibleChange(value: boolean): void {
        if (!value) this.cancelPolling();
        this.visible.set(value);
    }

    protected async generate(): Promise<void> {
        if (!this.canGenerate() || this.phase() === 'loading') return;
        this.cancelPolling();
        this.abort = new AbortController();
        this.phase.set('loading');
        this.error.set(null);
        this.candidates.set([]);
        try {
            const accepted = await this.aiAssist.generateBaseArt({
                jobId: crypto.randomUUID(),
                realmId: this.realmId(),
                templateId: this.templateId().trim(),
                version: this.version().trim(),
                referenceImagesB64: this.references().map((r) => r.b64),
                assetType: 'brand_icon',
                styleDescriptor: this.style(),
                mode: this.useManualPrompt() ? 'manual' : 'auto',
                prompt: this.useManualPrompt() ? this.prompt().trim() : null,
                samples: this.samples(),
            });
            const done = await this.aiAssist.awaitJob(accepted.job_id, {
                signal: this.abort.signal,
            });
            const result = done.result as BaseArtResult | null;
            this.candidates.set(result?.candidates ?? []);
            this.phase.set('results');
        } catch (err) {
            this.error.set(this.friendlyError(err));
            this.phase.set('form');
        }
    }

    protected use(candidate: BaseArtCandidate): void {
        this.applied.emit({
            logoUrl: candidate.url,
            faviconUrl: candidate.favicon?.url ?? candidate.url,
        });
        this.visible.set(false);
    }

    protected backToForm(): void {
        this.phase.set('form');
    }

    private cancelPolling(): void {
        this.abort?.abort();
        this.abort = null;
    }

    private reset(): void {
        this.cancelPolling();
        this.phase.set('form');
        this.error.set(null);
        this.candidates.set([]);
    }

    private friendlyError(err: unknown): string {
        if (!(err instanceof AiAssistApiError)) {
            return 'Could not reach the AI Assist service. Please try again.';
        }
        switch (err.code) {
            case 'vendor_timeout':
                return 'Generation took too long. Please try again.';
            case 'vendor_unavailable':
                return 'The image model is temporarily unavailable. Please try again shortly.';
            case 'rate_limited':
                return 'Too many requests right now. Please wait a moment and retry.';
            case 'content_moderated':
                return 'The request was blocked by the content filter. Try a different prompt or references.';
            case 'invalid_references':
                return 'One or more reference images could not be used (must be public, text-free images).';
            case 'unauthorized':
                return 'Your session is not authorized. Please sign in again.';
            case 'forbidden_realm':
                return 'You do not have access to this realm.';
            case 'invalid_request':
                return err.message ?? 'The request was rejected as invalid.';
            default:
                return err.message ?? 'Something went wrong. Please try again.';
        }
    }
}
